import { useCallback, useState } from "react"

import type { PanelProps } from "../panel-props"

import { useSelectSettings } from "../../../hooks/use-select-settings"
import { bookCache } from "../../../modules/image-cache/book-cache"
import { useSettings } from "../../../modules/settings"
import {
  setBookIndexes,
  useBookIndexes,
} from "../../../modules/speed-reader-books-store"
import {
  setSpeedReaderProgress,
  useSpeedReaderProgress,
} from "../../../modules/speed-reader-progress-store"
import { BookNavigator } from "../../../speed-reader/BookNavigator"
import { Badge } from "../../badge"
import { Button } from "../../button"
import { Select } from "../../select"
import { Slider } from "../../slider"
import { ToggleButton } from "../../toggle-button.tsx"
import { toast } from "../../ui/toast"
import { SharedSettings } from "../shared-settings.tsx"
import { useToggleAppSetting } from "../toggle-app-setting-hooks.ts"

export function SpeedReaderSettings(input: PanelProps) {
  const { appSettings, setAppSettings } = useSettings()
  const bookIndexes = useBookIndexes()
  const { bookIndex, progress, speed, totalItems } = appSettings.speedReader
  const storedIndex = progress[bookIndex] ?? 0
  const liveIndex = useSpeedReaderProgress(bookIndex) || storedIndex

  const clearBookCache = useCallback(()=> {
    bookCache.deleteAll().then(()=> {
      toast.success("Book cache cleared")
      bookCache.getBookIndexes().then(setBookIndexes)
    })
  }, [])

  const [altModeIsOn, toggleAltMode] = useToggleAppSetting(
    "speedReader",
    "altMode",
  )

  const [focusedChars, toggleFocusedChars] = useToggleAppSetting(
    "speedReader",
    "focusedChars",
  )

  const [abTesting, toggleAbTesting] = useToggleAppSetting(
    "speedReader",
    "abTesting",
  )

  const [showBookNav, setShowBookNav] = useState(false)

  const [waveModeIsOn, toggleWaveMode] = useToggleAppSetting(
    "speedReader",
    "waveMode",
  )

  const { onChange: handleBookChange, options: bookOptions } =
    useSelectSettings({
      options: bookIndexes,
      setValue: newValue=> {
        setAppSettings("bookIndex", newValue, "speedReader")
      },
    })

  const progressPermille =
    totalItems > 0 ? Math.round((liveIndex / totalItems) * 1000) : 0

  const onSpeedChange = useCallback(
    (newSpeed: number)=> {
      setAppSettings("speed", newSpeed, "speedReader")
    },
    [setAppSettings],
  )

  const onProgressChange = useCallback(
    (newPermille: number)=> {
      const newWordIndex =
        totalItems > 0 ? Math.floor((newPermille / 1000) * totalItems) : 0
      setAppSettings(
        "progress",
        { ...appSettings.speedReader.progress, [bookIndex]: newWordIndex },
        "speedReader",
      )
      setSpeedReaderProgress(bookIndex, newWordIndex)
    },
    [setAppSettings, progress, bookIndex, totalItems],
  )

  return (
    <SharedSettings closeSidebar={input.closeSidebar}>
      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Source</h3>
        <Select
          initialValue={bookIndex}
          label={"Selected book"}
          onChange={handleBookChange}
          options={bookOptions}
        />
      </section>

      {totalItems > 0 && (
        <>
          <div className="sidebar__separator" role="separator" />
          <section className="sidebar__section">
            <h3 className="sidebar__section-title">Progress</h3>
            <Slider
              label="Progress"
              max={1000}
              min={0}
              navigation={{ step: 5 }}
              onValueChange={onProgressChange}
              step={1}
              value={progressPermille}
            />
            <div>
              <Badge value={String(totalItems)} /> words
            </div>
          </section>
        </>
      )}

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Display</h3>
        <ToggleButton
          checked={altModeIsOn}
          label="Alt mode"
          onChange={toggleAltMode}
        />
        <ToggleButton
          checked={waveModeIsOn}
          label="Wave mode"
          onChange={toggleWaveMode}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Focus</h3>
        <ToggleButton
          checked={abTesting}
          label="AB Testing"
          onChange={toggleAbTesting}
        />
        <ToggleButton
          checked={focusedChars}
          label="Focused chars"
          onChange={toggleFocusedChars}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Playback</h3>
        <Slider
          label="Speed"
          max={2500}
          min={400}
          navigation={{ step: 10 }}
          onValueChange={onSpeedChange}
          step={10}
          value={speed}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Actions</h3>
        <Button onClick={clearBookCache}>Clear book cache</Button>
        <Button onClick={()=> setShowBookNav(true)}>
          Open book to navigate
        </Button>
        {showBookNav && (
          <BookNavigator
            bookIndex={bookIndex}
            onClose={()=> setShowBookNav(false)}
          />
        )}
      </section>
    </SharedSettings>
  )
}
